import { path } from 'ramda';
import {
    allIdentificationTypes,
    type ApiDriver,
    type ApiIdentification,
    type ApiTag,
    type ApiUser,
    type ApiUserGroup,
    type Driver,
    type Identification,
    parseDriverStatus,
    parseIdentificationType,
    type ResponseType,
    type Tag,
    type User,
    type UserGroup,
    type UserWithGroupIds,
} from '../types';

const EU_TACHOGRAPH_CARD = 'eu-tachograph-card';
const RIO_DRIVER_IDENTIFICATION = 'rio-driver-identification';
const COUNTRY_SPECIFIC_DRIVER_LICENSE = 'country-specific-driver-license';

const getActiveCard = (identifications: ApiIdentification[]) => {
    const latestEuTachographCard = getLatestEuTachographCard(identifications);
    const countrySpecificDriverLicense = getCountrySpecificDriverLicense(identifications);

    if (latestEuTachographCard !== '') {
        return latestEuTachographCard;
    } else if (countrySpecificDriverLicense !== '') {
        return countrySpecificDriverLicense;
    }
    return '';
};

function getCountrySpecificDriverLicense(identifications: ApiIdentification[]) {
    const countrySpecificDriverLicenses = identifications.filter(
        identification => identification.identification_type === COUNTRY_SPECIFIC_DRIVER_LICENSE
    );
    if (!countrySpecificDriverLicenses || countrySpecificDriverLicenses.length === 0) {
        return '';
    }
    return countrySpecificDriverLicenses[0].identification;
}

function getLatestEuTachographCard(identifications: ApiIdentification[]) {
    const tachographCards = identifications.filter(
        identification => identification.identification_type === EU_TACHOGRAPH_CARD
    );

    if (!tachographCards || tachographCards.length === 0) {
        return '';
    }

    return tachographCards.sort((c1, c2) => {
        const lastDigits1 = Number.parseInt(c1.identification.substring(c1.identification.length - 2), 36);
        const lastDigits2 = Number.parseInt(c2.identification.substring(c2.identification.length - 2), 36);
        return lastDigits1 - lastDigits2;
    })[tachographCards.length - 1].identification;
}

const getOtherCards = (identifications: ApiIdentification[]) => {
    const allCards = identifications.filter(
        identification => identification.identification_type !== RIO_DRIVER_IDENTIFICATION
    );

    if (!allCards || allCards.length <= 1) {
        return [];
    }

    const activeCard = getActiveCard(identifications);

    return allCards.filter(card => card.identification !== activeCard).map(inactiveCard => inactiveCard.identification);
};

const mapApiDriverToModel = (data: ApiDriver): Driver => {
    const identifications: ApiIdentification[] = path(['_embedded', 'identifications'], data) || [];
    const tags: ApiTag[] = path(['_embedded', 'tags'], data) || [];

    return {
        driverId: data.id,
        accountId: data.account_id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phoneNumber: data.phone_number,
        status: parseDriverStatus(data.status),
        subject: data.subject,
        tags: tags ? tags.map(tag => tag.id) : [],
        activeCardNumber: getActiveCard(identifications),
        otherCardNumbers: getOtherCards(identifications),
        identifications: identifications
            .filter(i => allIdentificationTypes.includes(i.identification_type))
            .map(i => mapApiIdentificationToModel(i)),
    };
};

const mapApiIdentificationToModel = (identification: ApiIdentification): Identification => {
    return {
        id: identification.id,
        value: identification.identification,
        type: parseIdentificationType(identification.identification_type),
    };
};

export const mapModelIdentificationToApi = (identification: Identification) => {
    return {
        id: identification.id,
        identification: identification.value,
        identification_type: identification.type,
    };
};

export const mapModelDriverToApi = (data: Driver): ApiDriver => ({
    id: data.driverId,
    account_id: data.accountId,
    first_name: data.firstName,
    last_name: data.lastName,
    phone_number: data.phoneNumber,
    email: data.email,
    subject: data.subject,
    status: data.status,
});

export const mapModelUserToApi = (user: UserWithGroupIds): ApiUser => ({
    id: user.id,
    account_id: user.accountId,
    first_name: user.firstName,
    last_name: user.lastName,
    email: user.email,
    phone_number: user.phoneNumber,
    group_ids: user.groupIds,
    preferred_language: user.preferredLanguage,
    preferred_login: user.email ? 'email' : 'phone',
});

export const mapApiUserToModel = (user: ApiUser) => {
    return {
        id: user.id,
        accountId: user.account_id,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        phoneNumber: user.phone_number,
        subject: user.subject,
    };
};

const mapUserToModel = (user: ApiUser): User => ({
    id: user.id,
    subject: user.subject,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    phoneNumber: user.phone_number,
});

const mapUserGroupToModel = (userGroup: ApiUserGroup): UserGroup => ({
    id: userGroup.id,
    name: userGroup.name,
});

const mapTagToModel = (tag: ApiTag): Tag => ({
    id: tag.id,
    type: tag.type,
    name: tag.name,
    accountId: tag.account_id,
});

export const mapModelTagToTagApi = (tagName: string, accountId: string): ApiTag => ({
    id: crypto.randomUUID(),
    type: 'user',
    name: tagName,
    account_id: accountId,
});

export const mapGetSingleDriverResponse = (response: ResponseType) => mapApiDriverToModel(response.body);

export const mapGetAllDriversResponse = (response: ResponseType) => {
    return response.body.items ? response.body.items.map(mapApiDriverToModel) : [];
};

export const mapAllUsersResponse = (response: ResponseType): User[] => {
    return response.body.items ? response.body.items.map((user: ApiUser) => mapUserToModel(user)) : [];
};

export const mapUserGroupResponse = (response: ResponseType) =>
    response.body.items.map((userGroup: ApiUserGroup) => mapUserGroupToModel(userGroup));

export const mapTagsResponse = (response: ResponseType) => response.body.items.map((tag: ApiTag) => mapTagToModel(tag));
